// The fixed-step clock: an accumulator fed with real time × time scale, drained in whole steps of a
// fixed size, with a cap on steps per frame and the leftover fraction exposed as the render
// interpolation alpha. Pure (no DOM, no rAF): the runtime drives it, tests call it directly.
//
// Rules (ai-context/discussions/vertical-slice/04-runtime.md):
// - The step size never changes. Time scale (slow motion, hit-stop) changes how much real time feeds
//   the accumulator, so a slowed sim takes fewer steps of the same size: collision and integration stay
//   exactly as stable as at full speed, and every system slows with no per-system code.
// - Sim time is `step × dt` from an integer step count, never a float that keeps growing (a growing
//   float clock shifts hit timings by a step between runs; swinging-hero teardown, feel §16).
// - The accumulator starts at half a step, so a request for a whole number of steps of real time
//   (`advance(100)` at 120 Hz = 12 steps) can't land on a rounding edge.
// - Hit-stop counts down in real time, and stacks by max, not by sum.
// - Two hit-stop modes. "scale" (the default, spike 001's): the whole clock slows to the hit-stop scale, view
//   included. "freeze" (the split hit-stop, spike 002): the sim takes no steps while it lasts, and the view dt
//   (`Tick.viewDt`, the frame phases' `dt`) ignores it, so poses, springs, VFX and the camera keep moving and input
//   keeps latching (suggestion "split hit-stop: freeze the sim, keep the view running").
// - Slow-mo is a second time layer (`slowMo(scale, seconds, easeBack)`): it multiplies the base time scale for the
//   sim and the view alike, holds its scale, then eases back to 1 over its last `easeBack` seconds as
//   scale + (1 - scale)(1 - k)^2; it counts down in real time, and a weaker request never cuts a stronger one
//   (suggestion "per-event slow-mo layer with an eased return"; Merge Conflict's stage.slowmo).

export interface ClockOptions {
  /** Fixed steps per second (composition setting: cozy 60, action 120). Default 60. */
  stepHz?: number;
  /** At most this many steps per frame; past it the backlog is dropped (slow motion, never a spiral). Default 10. */
  maxStepsPerFrame?: number;
  /** A frame's real dt is clamped to this many seconds (tab switches, breakpoints). Default 0.1. */
  maxFrameDt?: number;
  /** Start paused. */
  paused?: boolean;
  /** "scale" (default): hit-stop slows the whole clock to its scale. "freeze": hit-stop runs no sim steps and leaves
   *  the view dt alone (the split hit-stop). */
  hitStopMode?: HitStopMode;
}

/** How a hit-stop acts: slow the whole clock ("scale"), or freeze the sim and keep the view running ("freeze"). */
export type HitStopMode = "scale" | "freeze";

/** A slow-mo layer in force (real seconds). */
export interface SlowMoState {
  /** The time scale it holds. */
  scale: number;
  /** Real seconds left. */
  left: number;
  /** Its eased return: the last `ease` seconds go from `scale` back to 1. */
  ease: number;
}

/** Planted defects beside what they break (tests only). */
export interface ClockFaults {
  /** A freeze-mode hit-stop scales the whole clock to 0: the view dt is 0 too (the view freezes with the sim). */
  hitStopFreezesView: boolean;
  /** Overlapping hit-stops add up instead of taking the longest. */
  hitStopsSum: boolean;
}

/** What one frame (or one deterministic advance) did. */
export interface Tick {
  /** Real seconds this frame covered (after the clamp). */
  realDt: number;
  /** Real seconds × the time scale fed to the sim (0 while a freeze-mode hit-stop runs). */
  scaledDt: number;
  /** The view's dt: real seconds × the base time scale × slow-mo, ignoring a freeze-mode hit-stop (in "scale" mode the
   *  same as `scaledDt`). The frame phases' `dt`: poses, springs, VFX and the camera follow keep moving in a freeze. */
  viewDt: number;
  /** Fixed steps taken. */
  steps: number;
  /** Sim seconds advanced (steps × dt). */
  simDt: number;
  /** Render interpolation between the previous and the current step, 0..1. */
  alpha: number;
  /** Steps' worth of backlog dropped by the per-frame cap. */
  dropped: number;
}

/** A clock's full timing state (see `FixedStepClock.snapshot`). */
export interface ClockState {
  step: number;
  acc: number;
  hitStopLeft: number;
  hitStopScale: number;
  timeScale: number;
  /** The slow-mo layer in force (absent: none; saves from before slow-mo have none). */
  slowMo?: SlowMoState;
  /** The hit-stop in force was asked for with a scale (absent: it freezes, in "freeze" mode). */
  hitStopScaled?: boolean;
}

/** The time scale used when hit-stop is in force and none is given (swinging-hero: 0.06; exactly 0 freezes springs dead). */
export const HIT_STOP_SCALE = 0.06;

export class FixedStepClock {
  /** The fixed step, seconds. */
  readonly dt: number;
  readonly stepHz: number;
  readonly maxStepsPerFrame: number;
  readonly maxFrameDt: number;
  /** Base time scale: 1 normal, < 1 slow motion, 0 frozen (frames still draw), > 1 fast-forward. */
  timeScale = 1;
  /** Paused: frames take no steps; only `advance` moves the sim. */
  paused: boolean;
  private stepCount = 0;
  private acc: number;
  private hitStopLeft = 0;
  private hitStopScale = HIT_STOP_SCALE;
  /** In "freeze" mode, whether the hit-stop in force freezes (false: one asked for with an explicit scale). */
  private hitStopFreeze = true;
  private droppedTotal = 0;
  private slow: SlowMoState | null = null;
  /** How a hit-stop acts (see `HitStopMode`). */
  readonly hitStopMode: HitStopMode;
  /** Planted-defect switches (off in play). */
  readonly faults: ClockFaults = { hitStopFreezesView: false, hitStopsSum: false };

  constructor(o: ClockOptions = {}) {
    this.stepHz = o.stepHz ?? 60;
    if (!(this.stepHz > 0)) throw new Error(`stepHz must be > 0 (got ${this.stepHz})`);
    this.dt = 1 / this.stepHz;
    this.maxStepsPerFrame = Math.max(1, Math.floor(o.maxStepsPerFrame ?? 10));
    this.maxFrameDt = o.maxFrameDt ?? 0.1;
    this.paused = o.paused ?? false;
    this.hitStopMode = o.hitStopMode ?? "scale";
    this.acc = this.dt * 0.5;
  }

  /** Steps taken since construction (or the last `reset`). */
  get step(): number {
    return this.stepCount;
  }

  /** Sim seconds: step × dt. */
  get time(): number {
    return this.stepCount * this.dt;
  }

  /** The current interpolation alpha (the accumulator's leftover fraction of a step). */
  get alpha(): number {
    return Math.min(1, Math.max(0, this.acc / this.dt));
  }

  /** Real seconds of hit-stop still to run. */
  get hitStopRemaining(): number {
    return this.hitStopLeft;
  }

  /** Total steps dropped by the per-frame cap. */
  get droppedSteps(): number {
    return this.droppedTotal;
  }

  /** The slow-mo layer's factor now: its scale while it holds, easing back to 1 over its last `ease` seconds. */
  get slowMoScale(): number {
    const m = this.slow;
    if (!m || m.left <= 0) return 1;
    const k = m.ease > 0 ? Math.min(1, m.left / m.ease) : 1;
    return m.scale + (1 - m.scale) * (1 - k) * (1 - k);
  }

  /** Real seconds of slow-mo still to run. */
  get slowMoRemaining(): number {
    return this.slow?.left ?? 0;
  }

  /** The scale real time is fed to the sim at right now: the base scale × slow-mo, times the hit-stop scale while it
   *  runs (0 in "freeze" mode). */
  get effectiveScale(): number {
    const base = this.timeScale * this.slowMoScale;
    if (this.hitStopLeft <= 0) return base;
    return this.freezing() ? 0 : base * this.hitStopScale;
  }

  /** The scale the view runs at right now: in "freeze" mode the base scale × slow-mo, whatever the hit-stop (unless
   *  the planted defect freezes it too); in "scale" mode the sim's. */
  get viewScale(): number {
    if (this.hitStopMode !== "freeze" || (this.hitStopLeft > 0 && !this.hitStopFreeze)) return this.effectiveScale;
    if (this.faults.hitStopFreezesView && this.hitStopLeft > 0) return 0;
    return this.timeScale * this.slowMoScale;
  }

  /** Whether a hit-stop is in force (the sim frozen in "freeze" mode). */
  get frozen(): boolean {
    return this.hitStopLeft > 0;
  }

  /** Hit-stop for `seconds` of real time: "freeze" mode runs no sim steps meanwhile; "scale" mode slows the clock to
   *  `scale`. An explicit `scale` asks for a scaled hit-stop in either mode (the input's buffer check ages the buffer
   *  across slowed steps with it). Overlapping hit-stops keep the longer one (max, not sum). */
  hitStop(seconds: number, scale?: number): void {
    if (!(seconds > 0)) return;
    if (seconds >= this.hitStopLeft) {
      this.hitStopScale = scale ?? HIT_STOP_SCALE;
      this.hitStopFreeze = scale === undefined;
    }
    // the planted defect sits here, beside the max it breaks
    this.hitStopLeft = this.faults.hitStopsSum ? this.hitStopLeft + seconds : Math.max(this.hitStopLeft, seconds);
  }

  /** Slow the sim and the view to `scale` for `seconds` of real time, easing back to 1 over the last `easeBack`
   *  seconds (default 30 % of `seconds`). A request weaker than the slow-mo in force now is ignored. */
  slowMo(scale: number, seconds: number, easeBack = 0.3 * seconds): void {
    if (!(seconds > 0) || !(scale >= 0) || scale >= 1) return;
    if (this.slow && this.slow.left > 0 && scale > this.slowMoScale) return;
    this.slow = { scale, left: seconds, ease: Math.min(seconds, Math.max(0, easeBack)) };
  }

  /** Everything that decides when the next steps run (a save mid-fight stores it: a hit-stop in force or a
   *  part-filled accumulator changes which real frame the next step lands in). Plain numbers, JSON-safe. */
  snapshot(): ClockState {
    const out: ClockState = { step: this.stepCount, acc: this.acc, hitStopLeft: this.hitStopLeft, hitStopScale: this.hitStopScale, timeScale: this.timeScale };
    if (!this.hitStopFreeze) out.hitStopScaled = true;
    if (this.slow && this.slow.left > 0) out.slowMo = { ...this.slow };
    return out;
  }

  /** Restore a snapshot exactly (the dropped-step count is a statistic and is kept). */
  restore(s: ClockState): void {
    this.stepCount = s.step;
    this.acc = s.acc;
    this.hitStopLeft = s.hitStopLeft;
    this.hitStopScale = s.hitStopScale;
    this.timeScale = s.timeScale;
    this.hitStopFreeze = s.hitStopScaled !== true;
    this.slow = s.slowMo ? { ...s.slowMo } : null;
  }

  /** Back to step 0, half a step in the accumulator, no hit-stop (a replay or capture starts here). */
  reset(step = 0): void {
    this.stepCount = step;
    this.acc = this.dt * 0.5;
    this.hitStopLeft = 0;
    this.slow = null;
  }

  /** A freeze-mode hit-stop started by a step holds the steps still banked in this frame (they run after it). */
  private holding(): boolean {
    return this.hitStopLeft > 0 && this.freezing();
  }

  /** The hit-stop in force (if any) freezes the sim: "freeze" mode, and not asked for with a scale. */
  private freezing(): boolean {
    return this.hitStopMode === "freeze" && this.hitStopFreeze;
  }

  /** Count real time off the hit-stop and the slow-mo; returns the sim's and the view's scaled seconds for it. */
  private spend(real: number): { sim: number; view: number } {
    let sim = 0;
    let view = 0;
    let left = real;
    // in "freeze" mode, in slices that end where the hit-stop ends, so a frame that straddles its end feeds the rest at
    // full scale; "scale" mode keeps its rule: the whole frame at the scale in force when it starts
    const split = this.hitStopMode === "freeze";
    while (left > 1e-12) {
      const slice = split && this.hitStopLeft > 0 && this.hitStopLeft < left ? this.hitStopLeft : left;
      sim += slice * this.effectiveScale;
      view += slice * this.viewScale;
      this.hitStopLeft = this.hitStopLeft - slice > 1e-12 ? this.hitStopLeft - slice : 0;
      if (this.slow) {
        this.slow.left = this.slow.left - slice > 1e-12 ? this.slow.left - slice : 0;
        if (this.slow.left <= 0) this.slow = null;
      }
      left -= slice;
    }
    return { sim, view };
  }

  /**
   * One rendered frame of `realDt` seconds. Calls `onStep(dt, time)` once per fixed step (time is the sim time
   * at the start of that step). Paused: no steps (alpha holds).
   */
  frame(realDt: number, onStep: (dt: number, time: number) => void): Tick {
    const real = Math.min(Math.max(0, Number.isFinite(realDt) ? realDt : 0), this.maxFrameDt);
    if (this.paused) return { realDt: real, scaledDt: 0, viewDt: 0, steps: 0, simDt: 0, alpha: this.alpha, dropped: 0 };
    const fed = this.spend(real);
    this.acc += fed.sim;
    let steps = 0;
    while (this.acc >= this.dt && steps < this.maxStepsPerFrame && !this.holding()) {
      onStep(this.dt, this.time);
      this.stepCount++;
      this.acc -= this.dt;
      steps++;
    }
    let dropped = 0;
    if (this.acc >= this.dt && !this.holding()) {
      // over the cap: drop whole steps of backlog, keep the fraction so alpha stays continuous
      dropped = Math.floor(this.acc / this.dt);
      this.acc -= dropped * this.dt;
      this.droppedTotal += dropped;
    }
    return { realDt: real, scaledDt: fed.sim, viewDt: fed.view, steps, simDt: steps * this.dt, alpha: this.alpha, dropped };
  }

  /**
   * Deterministic time for tests and captures: feed `ms` of real time through the same scaled accumulator
   * as play, in slices of at most one step, so a hit-stop that starts mid-advance takes effect at once, as
   * it would in play. Works while paused. At time scale 1 it takes round(ms / step) steps for any whole
   * number of steps' worth of ms. No per-frame cap (a test asked for this much time).
   */
  advance(ms: number, onStep: (dt: number, time: number) => void): Tick {
    let remaining = Math.max(0, ms / 1000);
    const real = remaining;
    let scaled = 0;
    let view = 0;
    let steps = 0;
    while (remaining > 1e-9) {
      const slice = Math.min(remaining, this.dt);
      remaining -= slice;
      const fed = this.spend(slice);
      this.acc += fed.sim;
      scaled += fed.sim;
      view += fed.view;
      while (this.acc >= this.dt && !this.holding()) {
        onStep(this.dt, this.time);
        this.stepCount++;
        this.acc -= this.dt;
        steps++;
      }
    }
    return { realDt: real, scaledDt: scaled, viewDt: view, steps, simDt: steps * this.dt, alpha: this.alpha, dropped: 0 };
  }
}
